/* The fourth feedback loop: what the reviewer decided, as a file they can read.
 *
 * The one thing that would make these checks better is knowing which findings
 * were not worth a comment and why. There is exactly one honest way to ask for
 * that in a product whose entire credibility rests on "nothing leaves your
 * browser": build the file locally, hand it to the reviewer, and let them
 * decide whether to send it to anyone. Nothing here posts, beacons, or
 * schedules anything. There is no endpoint. tools/network-check.ts proves it
 * for the whole review path, not just for this module.
 *
 * The document itself is the other half of that promise. By default the export
 * carries no text from the plan under review: check identity, the reviewer's
 * own reasons and notes, and the timestamps. The quoted clause and the comment
 * drafted from it are derived from the document, so they are behind a tick box
 * that starts empty and is never pre-ticked.
 *
 * The header block — project number, contract number, the reviewer's own name,
 * phone and email — is not in either form of the export. It was never asked
 * for, it identifies a project and a person, and a file offered for sharing
 * should contain only what the sentence above it says it contains.
 */

import { evidenceBadgeFor } from '../data/report';
import { commentFor, locationChip } from '../data/review';
import type {
  Disposition,
  Flag,
  MutedChecks,
  ReviewRecord,
} from '../types';

/**
 * Where a reviewer can send the file if they want to. The only address in the
 * product; change it here and it changes on the comment sheet with it.
 */
export const MAINTAINER_EMAIL = 'namanpuri21767@gmail.com';

/** The schema tag, so a file received later can be read without guessing. */
export const DECISIONS_SCHEMA = 'ceiling.decisions.v1';

/**
 * One decision. Everything above `location` is either about the check or in the
 * reviewer's own words; the three fields below it come from the document and
 * are present only when the reviewer asked for them.
 */
export interface DecisionEntry {
  /** Which check produced the finding, e.g. "reconciliation-check". */
  checkId: string;
  /** Which failure mode, e.g. "FM-15". */
  check: string;
  planArea: string;
  tier: 1 | 2;
  evidence: string;
  decision: Disposition['decision'];
  /** Present on "not a concern". This is the field worth reading. */
  reason?: string;
  /** The reviewer's own words, where the reason was "something else". */
  reasonText?: string;
  /** The reviewer's own note, whatever the decision. */
  note?: string;
  /** Who a referred finding went to, where they named anyone. */
  referredTo?: string;
  decidedAt: string;
  /** Whether the reviewer rewrote the drafted comment before sending it. */
  commentEdited: boolean;

  /* --- from the document, and only with the box ticked ------------------- */

  location?: string;
  quote?: string;
  comment?: string;
}

export interface DecisionsExport {
  schema: typeof DECISIONS_SCHEMA;
  generatedAt: string;
  /** What this file does and does not contain, inside the file itself. */
  about: string;
  includesQuotedClauses: boolean;
  findingCount: number;
  decidedCount: number;
  decisions: DecisionEntry[];
  /** Checks this browser has muted, which is the same signal at a longer range. */
  mutedChecks: {
    check: string;
    /**
     * How it came to be muted: 'dismissals' after the same reason recurred in
     * reviews, 'reference' where the reviewer switched it off on /checks
     * without ever setting a finding aside. The reason and the count below
     * mean something only in the first case, and this says which case it is.
     */
    source: 'dismissals' | 'reference';
    reason: string;
    count: number;
    mutedAt: string;
  }[];
}

const ABOUT_WITHOUT_CLAUSES =
  'Decisions from one Ceiling review. Check identifiers, the reviewer’s reasons and notes, and timestamps. No text from the reviewed document, and no project or reviewer identification. Produced in the browser; nothing was transmitted.';

const ABOUT_WITH_CLAUSES =
  'Decisions from one Ceiling review, including the clause each finding quoted and the review comment drafted from it, because the reviewer asked for them. No project or reviewer identification. Produced in the browser; nothing was transmitted.';

/** Empty strings are left out rather than exported as empty fields. */
function orUndefined(value: string | undefined): string | undefined {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
}

export interface DecisionsOptions {
  /** The tick box. Never defaulted to true anywhere. */
  includeClauses?: boolean;
  now?: string;
}

export function decisionsExport(
  findings: Flag[],
  review: ReviewRecord | null,
  muted: MutedChecks = {},
  options: DecisionsOptions = {},
): DecisionsExport {
  const includeClauses = options.includeClauses === true;
  const dispositions = review?.dispositions ?? {};
  const edited = review?.editedComments ?? {};

  const decisions: DecisionEntry[] = findings
    .filter((finding) => dispositions[finding.id] !== undefined)
    .map((finding) => {
      const d = dispositions[finding.id];
      const entry: DecisionEntry = {
        checkId: finding.checkId,
        check: finding.failureModeId,
        planArea: finding.planArea,
        tier: finding.tier,
        evidence: evidenceBadgeFor(finding).id,
        decision: d.decision,
        reason: d.reason,
        reasonText: orUndefined(d.reasonText),
        note: orUndefined(d.note),
        referredTo: orUndefined(d.referredTo),
        decidedAt: d.decidedAt,
        commentEdited: edited[finding.id] !== undefined,
      };

      if (includeClauses) {
        entry.location = locationChip(finding.sectionRef, finding.page) ?? undefined;
        entry.quote = finding.quote ?? undefined;
        entry.comment = commentFor(
          { editedComments: edited },
          finding.id,
          finding.commentDraft,
        );
      }

      return entry;
    });

  return {
    schema: DECISIONS_SCHEMA,
    generatedAt: options.now ?? new Date().toISOString(),
    about: includeClauses ? ABOUT_WITH_CLAUSES : ABOUT_WITHOUT_CLAUSES,
    includesQuotedClauses: includeClauses,
    findingCount: findings.length,
    decidedCount: decisions.length,
    decisions,
    mutedChecks: Object.values(muted)
      .filter((m): m is NonNullable<typeof m> => Boolean(m))
      .map((m) => ({
        check: m.failureModeId,
        source: m.source ?? 'dismissals',
        reason: m.reason,
        count: m.count,
        mutedAt: m.mutedAt,
      })),
  };
}

/**
 * The file's bytes. Indented, because a reviewer who is being asked to share
 * something should be able to open it and read what is in it before they do.
 *
 * `undefined` fields are dropped by JSON.stringify, which is why an absent note
 * is absent rather than null — there is a difference between "no note" and "a
 * note that is empty", and only the first ever happens.
 */
export function decisionsJson(data: DecisionsExport): string {
  return `${JSON.stringify(data, null, 2)}\n`;
}
